import { Button, Col, Input, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IrreversableModal } from 'screens/Admin/components';
import { fmtCount, resolveTapTargetBounds, validateTapTarget } from '../constants';
import { blockNonInteger, ResolvedTarget, Section, toDigits, Toggle } from './ui';

// Per-channel tap-target override (P4P Admin). Writes the highest-precedence `p4pTarget`
// and optionally locks the value so the Team Admin can't change it.
// Backend: GET /admin/fanwave/config, PUT /admin/fanwave/setTarget, POST /admin/fanwave/applyTarget.
const ChannelConfigSection = ({ team }) => {
  const dispatch = useDispatch();
  const teamId = team?._id;

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [target, setTarget] = useState('');
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    const res = await Api.get(`/admin/fanwave/config?teamId=${teamId}`, null, { silent: true });
    if (res?.data) {
      setConfig(res.data);
      // Seed the input with the P4P override if one exists, else the resolved target.
      setTarget(res.data.source?.p4pTarget ?? res.data.tapTarget ?? '');
      setLocked(!!res.data.locked);
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Bounds come off the resolved config this screen already fetched, so a server-side change to
  // the floor/step (both due to return to 500) lands here without a release — same source the
  // create/update form validates against.
  const targetBounds = resolveTapTargetBounds(config);
  const targetError = validateTapTarget(target, targetBounds);

  const handleSave = async () => {
    if (targetError) return;
    setSaving(true);
    const res = await Api.put('/admin/fanwave/setTarget', {
      teamId,
      target: Number(target),
      locked
    });
    setSaving(false);
    if (res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Channel target saved.' } });
      fetchConfig();
    }
  };

  const handleApply = async () => {
    setApplying(true);
    const res = await Api.post('/admin/fanwave/applyTarget', {
      teamId,
      target: Number(target),
      confirm: true
    });
    setApplying(false);
    setApplyOpen(false);
    if (res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Target applied to the active FanWave.' } });
    }
  };

  const handleReset = async () => {
    setResetting(true);
    const res = await Api.post('/admin/fanwave/resetTarget', { teamId });
    setResetting(false);
    setResetOpen(false);
    if (res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Channel reset to global default.' } });
      fetchConfig();
    }
  };

  // A channel has an override to reset only when a team-scoped value or lock exists.
  const hasOverride =
    config != null &&
    (config.source?.p4pTarget != null || config.source?.teamTarget != null || config.locked);

  if (!teamId) return null;

  const source = config?.source || {};

  return (
    <Section
      title="Channel Target"
      description="Override the global default for this channel. As P4P Admin you can also lock the value so the Team Admin cannot change it."
    >
      {loading || !config ? (
        <Row center pad="8px 0">
          <Spinner2 />
        </Row>
      ) : (
        <>
          {/* Resolved-config breakdown so the admin can see what's currently in effect. */}
          <ResolvedTarget tapTarget={config.tapTarget} source={source} locked={config.locked} />

          {/* Editable override + lock — paired control cards of equal height. */}
          <Row gap="14px" style={{ alignItems: 'stretch' }}>
            <Col
              noFlex
              pad="14px 16px"
              style={{
                flex: '1 1 240px',
                minWidth: '220px',
                background: theme.GREY_10,
                borderRadius: '12px',
                border: `1px solid ${theme.GREY_20}`
              }}
            >
              <Text b2 bold marg="0 0 8px" style={{ fontSize: '13px' }}>
                P4P override target
              </Text>
              <Input
                type="number"
                inputMode="numeric"
                value={target}
                onChange={(e) => setTarget(toDigits(e.target.value))}
                onKeyDown={blockNonInteger}
                placeholder="Override target"
                step={targetBounds.step}
              />
              <Text
                c1
                col={targetError ? theme.SECONDARY : theme.GREY_60}
                marg="7px 0 0"
                style={{ fontSize: '11px' }}
              >
                {targetError ||
                  `Min ${fmtCount(targetBounds.min)}, max ${fmtCount(targetBounds.max)}, step ${fmtCount(
                    targetBounds.step
                  )}.`}
              </Text>
            </Col>

            <Col
              noFlex
              pad="14px 16px"
              style={{
                flex: '1 1 240px',
                minWidth: '220px',
                background: theme.GREY_10,
                borderRadius: '12px',
                border: `1px solid ${theme.GREY_20}`
              }}
            >
              <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
                Lock for Team Admin
              </Text>
              <Row center noFlex marg="10px 0 0">
                <Toggle on={locked} onToggle={() => setLocked((v) => !v)} />
                <Text b2 col={theme.GREY_60} marg="0 0 0 10px" style={{ fontSize: '12px' }}>
                  {locked ? "Team Admin can't change" : 'Team Admin may set own'}
                </Text>
              </Row>
              <Text
                c1
                col={theme.GREY_60}
                marg="auto 0 0"
                style={{ fontSize: '11px', paddingTop: '10px' }}
              >
                {locked
                  ? 'P4P has locked this channel; only you can change the target.'
                  : 'Lock to stop the Team Admin overriding this target.'}
              </Text>
            </Col>
          </Row>

          <Row start marg="16px 0 0">
            <Button
              text={saving ? 'Saving…' : 'Save changes'}
              onClick={handleSave}
              bg={theme.SECONDARY}
              loading={saving}
              disabled={!!targetError || saving}
              small
              wid="150px"
            />
          </Row>

          {/* Higher-consequence actions, set apart in their own panel. */}
          <Col
            noFlex
            wid="100%"
            marg="18px 0 0"
            pad="14px 16px"
            style={{
              background: theme.GREY_10,
              borderRadius: '12px',
              border: `1px solid ${theme.GREY_20}`
            }}
          >
            <Text b2 bold marg="0 0 3px" style={{ fontSize: '13px' }}>
              Live &amp; reset
            </Text>
            <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '11.5px', lineHeight: 1.45 }}>
              “Apply live” rescales the running wave for all fans (mid-match only). “Reset” clears
              this channel’s override and lock.
            </Text>
            <Row start gap="8px" marg="12px 0 0">
              <Button
                text="Apply live"
                onClick={() => !targetError && setApplyOpen(true)}
                bordered
                disabled={!!targetError}
                small
                wid="110px"
              />
              <Button
                text="Reset"
                onClick={() => setResetOpen(true)}
                bordered
                disabled={!hasOverride}
                small
                wid="100px"
              />
            </Row>
          </Col>
        </>
      )}

      <IrreversableModal
        visible={applyOpen}
        heading="Apply to active FanWave?"
        text={`This rescales the live progress bar to a target of ${target} and broadcasts it to all connected fans immediately.`}
        confirmText={applying ? 'Applying…' : 'Apply now'}
        waiting={applying}
        onConfirm={handleApply}
        closeModal={() => !applying && setApplyOpen(false)}
        confirmNoClose
      />

      <IrreversableModal
        visible={resetOpen}
        heading="Reset channel to global default?"
        text={`This removes this channel's tap-target override${
          config?.locked ? ' and lock' : ''
        }. FanWave will follow the global default (${source.globalTarget ?? '—'}).`}
        confirmText={resetting ? 'Resetting…' : 'Reset'}
        waiting={resetting}
        onConfirm={handleReset}
        closeModal={() => !resetting && setResetOpen(false)}
        confirmNoClose
      />
    </Section>
  );
};

export default ChannelConfigSection;
